import { DuplicateBanner } from '@components'

import { ModuleLayout, Space, useModal } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { DuplicatedReceiptsModal } from '@features/attachments/elements/DuplicatedReceiptsModal'
import { EInvoicingSetupContextProvider } from '@features/eInvoicing/context/EInvoicingSetupContext'
import { EInvoicingDashboardBanner } from '@features/eInvoicing/elements/EinvoicingDashboardBanner'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'
import { useDuplicateAttachments } from '@views/receipts/hooks/useDuplicateAttachments'

import { ModalId } from '../../../../../enums/modalId'
import { useSegment } from '../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { BillsSideActions } from './elements/BillsSideActions'
import { BillsSummary } from './elements/BillsSummary'
import { BillsTable } from './elements/BillsTable'
import { BillsTableActions } from './elements/BillsTableActions'
import { ReceiptsToFill } from './elements/ReceiptsToFill'
import { useBillsListFilters } from './hooks/useBillsListFilters'
import { useIsBillsListEmpty } from './hooks/useIsBillsListEmpty'

export const BillsListLegacy = (): ReactElement | null => {
  const { t } = useTranslation()
  const isBillsListEmpty = useIsBillsListEmpty()
  const [{ contactId, contactName }] = useBillsListFilters()
  const { url: billsListEmptyUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getListEmptyRoute())
  const { duplicates } = useDuplicateAttachments()
  const { open: openDuplicateAttachmentsModal } = useModal(ModalId.DuplicatedReceiptsActionModal)
  const { track } = useSegment()

  const handleDuplicateReviewClick = () => {
    track(ExpensesTrackingEvents.DuplicateReviewClicked)
    openDuplicateAttachmentsModal()
  }

  const title = useMemo(
    () => (contactId && contactName ? t('billsFrom', { contactName }) : t('bills')),
    [contactId, contactName, t],
  )

  if (!billsListEmptyUrl) {
    return null
  }

  if (isBillsListEmpty) {
    return <Navigate to={billsListEmptyUrl} replace />
  }

  return (
    <>
      <ModuleLayout sideActions={<BillsSideActions />} title={title}>
        {duplicates.length > 0 && <DuplicateBanner onPress={handleDuplicateReviewClick} />}
        <ReceiptsToFill />
        <EInvoicingSetupContextProvider>
          <EInvoicingDashboardBanner source="bills-list" />
        </EInvoicingSetupContextProvider>
        <Space size="s32" />
        <BillsSummary />
        <BillsTableActions />
        <BillsTable />
      </ModuleLayout>
      <DuplicatedReceiptsModal id={ModalId.DuplicatedReceiptsActionModal} duplicates={duplicates} />
    </>
  )
}
